import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material';
import { NgxSpinnerService } from 'ngx-spinner';
import { GatewayService } from 'src/app/gateway.service';
import { LocalSaveService } from 'src/app/services/local-save-service.service';

type QueryKind = 'HFC' | 'FTTH' | 'TV' | 'TEL';

@Component({
  selector: 'app-consulta-certificado-nacimieto',
  templateUrl: './consulta-certificado-nacimieto.component.html',
  styleUrls: ['./consulta-certificado-nacimieto.component.scss']
})
export class ConsultaCertificadoNacimietoComponent implements OnInit {

  public loading: { [K in QueryKind]: boolean } = {
    HFC: false,
    FTTH: false,
    TV: false,
    TEL: false
  };

  public hfcResult: any = null;
  public ftthResult: any = null;
  public tvResult: any = null;
  public telephonyResult: any = null;

  public formHFC: FormGroup = new FormGroup({
    deviceId: new FormControl('', [Validators.required])
  });

  public formFTTH: FormGroup = new FormGroup({
    sn: new FormControl('', [Validators.required])
  });

  public formTV: FormGroup = new FormGroup({
    sn: new FormControl('', [Validators.required])
  });

  public formTelephony: FormGroup = new FormGroup({
    phone: new FormControl('', [Validators.required])
  });

  private getCommonParams() {
    return {
      TECNICO: this.getUserId(),
      TIPO: 2
    };
  }

  private queryConfig: any = {
    HFC: {
      form: () => this.formHFC,
      buildParams: () => ({
        deviceId: this.formHFC.controls.deviceId.value,
        ...this.getCommonParams()
      }),
      request: (params) => this.gateway.certificarHFC(params),
      setResult: (result) => this.hfcResult = result,
      getResult: () => this.hfcResult,
      successMsg: 'Consulta HFC exitosa',
      emptyMsg: 'No se encontraron resultados para HFC',
      errorMsg: 'Error al realizar la consulta HFC'
    },
    FTTH: {
      form: () => this.formFTTH,
      buildParams: () => ({
        sn: this.formFTTH.controls.sn.value,
        ...this.getCommonParams()
      }),
      request: (params) => this.gateway.certificarFTTH(params),
      setResult: (result) => this.ftthResult = result,
      getResult: () => this.ftthResult,
      successMsg: 'Consulta FTTH exitosa',
      emptyMsg: 'No se encontraron resultados para FTTH',
      errorMsg: 'Error al realizar la consulta FTTH'
    },
    TV: {
      form: () => this.formTV,
      buildParams: () => ({
        sn: this.formTV.controls.sn.value,
        ...this.getCommonParams()
      }),
      request: (params) => this.gateway.certificarTV(params),
      setResult: (result) => this.tvResult = result,
      getResult: () => this.tvResult,
      successMsg: 'Consulta TV exitosa',
      emptyMsg: 'No se encontraron resultados para TV',
      errorMsg: 'Error al realizar la consulta TV'
    },
    TEL: {
      form: () => this.formTelephony,
      buildParams: () => ({
        telefono: this.formTelephony.controls.phone.value,
        ...this.getCommonParams()
      }),
      request: (params) => this.gateway.certificarTelefonia(params),
      setResult: (result) => this.telephonyResult = result,
      getResult: () => this.telephonyResult,
      successMsg: 'Consulta Telefonía exitosa',
      emptyMsg: 'No se encontraron resultados para Telefonía',
      errorMsg: 'Error al realizar la consulta Telefonía'
    }
  };

  constructor(
    private gateway: GatewayService,
    private snackBar: MatSnackBar,
    private localSave: LocalSaveService
  ) { }

  ngOnInit() {
  }

  private getUserId(): string {
    const usuario = this.localSave.get('usuarioPGV');
    if (usuario && usuario.IDU) {
      return usuario.IDU;
    }
    if (usuario && usuario.idagente) {
      return usuario.idagente;
    }
    return '';
  }

  public runQuery(kind: QueryKind): void {
    const cfg = this.queryConfig[kind];
  
    if (!cfg) { return; }

    if (this.loading[kind]) { return; } 
  
    const form = cfg.form();
    if (!form || form.invalid) { return; }
  
    this.loading[kind] = true;
  
    const params = cfg.buildParams();
  
    cfg.request(params).subscribe(
      (result: any) => {
        this.loading[kind] = false;
  
        if (result) {
          cfg.setResult(result);
          this.snackBar.open(cfg.successMsg, 'Cerrar', { duration: 3000 });
        } else {
          cfg.setResult(null);
          this.snackBar.open(cfg.emptyMsg, 'Cerrar', { duration: 3000 });
        }
      },
      (error) => {
        this.loading[kind] = false;
  
        this.snackBar.open(cfg.errorMsg, 'Cerrar', { duration: 3000 });
      }
    );
  }  

  public clearQuery(kind: QueryKind): void {
    const cfg = this.queryConfig[kind];
    if (!cfg) { return; }

    cfg.setResult(null);
    const form = cfg.form();
    if (form) { form.reset(); }
  }

  // --- HFC formatted text getters ---

  public getHfcProvisioningText(): string {
    if (!this.hfcResult || !this.hfcResult.provisioning) { return ''; }
    let p = this.hfcResult.provisioning;
    let lines = [];
    lines.push('--- Provisionamiento ---');
    if (p.regStatus) { lines.push('Estado: ' + p.regStatus); }
    if (p.cmProvisioned === true || p.cmProvisioned === 'true') {
      lines.push('Provisionado');
    } else {
      lines.push('No provisionado');
    }
    return lines.join('\n');
  }

  public getHfcSoftwareText(): string {
    if (!this.hfcResult || !this.hfcResult.software) { return ''; }
    const s = this.hfcResult.software;
    let lines = [];
    lines.push('--- Software ---');
    if (s.sw_string) { lines.push(s.sw_string); }
    if (s.pass === true || s.pass === 1) {
      lines.push('Version correcta');
    } else if (s.pass === false || s.pass === 0) {
      lines.push('Version incorrecta');
    }
    return lines.join('\n');
  }

  public getHfcResumenText(): string {
    if (!this.hfcResult) { return ''; }
    let lines = [];
    lines.push('--- Resumen ---');
    lines.push(this.hfcResult.resumen || this.hfcResult.summary || '');
    return lines.join('\n');
  }

  public copyAllHFC(): void {
    if (!this.hfcResult) { return; }
    let sections = [
      this.getHfcProvisioningText(),
      this.getHfcSoftwareText(),
      this.getHfcResumenText()
    ].filter(function(s) { return s && s.length > 0; });
    this.copyToClipboard(sections.join('\n\n'), 'Todos los resultados HFC copiados al portapapeles');
  }

  // --- FTTH formatted text getters ---

  public getFtthTipoEquipoText(): string {
    if (!this.ftthResult || !this.ftthResult.parametros) { return ''; }
    let p = this.ftthResult.parametros;
    let lines = [];
    lines.push('--- Tipo de equipo ---');
    if (p['soft-version'] && p['soft-version'].valor) {
      lines.push('Version de software: ' + p['soft-version'].valor);
      if (p['soft-version'].pass === true || p['soft-version'].pass === 1) {
        lines.push('Version correcta');
      } else if (p['soft-version'].pass === false || p['soft-version'].pass === 0) {
        lines.push('Version incorrecta');
      }
    }
    return lines.join('\n');
  }

  public getFtthPerfilesText(): string {
    if (!this.ftthResult || !this.ftthResult.parametros) { return ''; }
    let p = this.ftthResult.parametros;
    let lines = [];
    lines.push('--- Perfiles Asociados ---');
    if (p.lineprof && p.lineprof.valor) { lines.push('Line Profile: ' + p.lineprof.valor); }
    if (p.servprof && p.servprof.valor) { lines.push('Service Profile: ' + p.servprof.valor); }
    if (p.vaprof && p.vaprof.valor) { lines.push('ONU General VAS Profile: ' + p.vaprof.valor); }
    return lines.join('\n');
  }

  public getFtthOpticosText(): string {
    if (!this.ftthResult || !this.ftthResult.parametros) { return ''; }
    let p = this.ftthResult.parametros;
    let lines = [];
    lines.push('--- Parametros Opticos ---');
    if (p.opticsrxpower && p.opticsrxpower.valor_dBm !== undefined) {
      lines.push('RX Power: ' + p.opticsrxpower.valor_dBm);
      if (p.opticsrxpower.rango_min !== undefined) {
        lines.push('(rango permitido: ' + p.opticsrxpower.rango_min + ' a ' + p.opticsrxpower.rango_max + ')');
      }
    }
    if (p.opticstxpower && p.opticstxpower.valor_dBm !== undefined) {
      lines.push('TX Power: ' + p.opticstxpower.valor_dBm);
      if (p.opticstxpower.rango_min !== undefined) {
        lines.push('(rango permitido: ' + p.opticstxpower.rango_min + ' a ' + p.opticstxpower.rango_max + ')');
      }
    }
    return lines.join('\n');
  }

  public getFtthResumenText(): string {
    if (!this.ftthResult) { return ''; }
    let lines = [];
    lines.push('--- Resumen ---');
    lines.push(this.ftthResult.resumen || this.ftthResult.summary || '');
    return lines.join('\n');
  }

  public copyAllFTTH(): void {
    if (!this.ftthResult) { return; }
    let sections = [
      this.getFtthTipoEquipoText(),
      this.getFtthPerfilesText(),
      this.getFtthOpticosText(),
      this.getFtthResumenText()
    ].filter(function(s) { return s && s.length > 0; });
    this.copyToClipboard(sections.join('\n\n'), 'Todos los resultados FTTH copiados al portapapeles');
  }

  // --- TV formatted text getters ---

  public getTvParametrosText(): string {
    if (!this.tvResult || !this.tvResult.parametros) { return ''; }
    let p = this.tvResult.parametros;
    let lines = [];
    lines.push('--- Parametros ---');
    if (p.sn && p.sn.valor) { lines.push('Serial Number: ' + p.sn.valor); }
    if (p.ssid && p.ssid.valor !== undefined) { lines.push('SSID: ' + p.ssid.valor); }
    if (p.ipAddr && p.ipAddr.valor !== undefined) { lines.push('IP Address: ' + p.ipAddr.valor); }
    if (p.brand && p.brand.valor !== undefined) { lines.push('Marca: ' + p.brand.valor); }
    if (p.model && p.model.valor !== undefined) { lines.push('Modelo: ' + p.model.valor); }
    if (p.cpuTemp && p.cpuTemp.valor !== undefined) { lines.push('Temperatura CPU: ' + p.cpuTemp.valor); }
    if (p.wifiSignalStrength && p.wifiSignalStrength.valor !== undefined) { lines.push('Señal WiFi: ' + p.wifiSignalStrength.valor); }
    if (p.bluetoothSignalStrength && p.bluetoothSignalStrength.valor !== undefined) { lines.push('Señal Bluetooth: ' + p.bluetoothSignalStrength.valor); }
    if (p.active) {
      if (p.active.pass === true || p.active.pass === 1) { lines.push('Activo: Si'); }
      else { lines.push('Activo: No'); }
      if (p.active.valor !== undefined) { lines.push('Valor: ' + (p.active.valor == true ? 'Si' : 'No')); }
      if (p.active.esperado !== undefined) { lines.push('Esperado: ' + (p.active.esperado == true ? 'Si' : 'No')); }
    }
    if (p.lock) {
      if (p.lock.pass === true || p.lock.pass === 1) { lines.push('Bloqueado: Correcto'); }
      else { lines.push('Bloqueado: Incorrecto'); }
      if (p.lock.valor !== undefined) { lines.push('Valor: ' + (p.lock.valor == true ? 'Si' : 'No')); }
      if (p.lock.esperado !== undefined) { lines.push('Esperado: ' + (p.lock.esperado == true ? 'Si' : 'No')); }
    }
    return lines.join('\n');
  }

  public getTvResumenText(): string {
    if (!this.tvResult) { return ''; }
    let lines = [];
    lines.push('--- Resumen ---');
    lines.push(this.tvResult.resumen || this.tvResult.summary || '');
    return lines.join('\n');
  }

  public copyAllTV(): void {
    if (!this.tvResult) { return; }
    let sections = [
      this.getTvParametrosText(),
      this.getTvResumenText()
    ].filter(function(s) { return s && s.length > 0; });
    this.copyToClipboard(sections.join('\n\n'), 'Todos los resultados TV copiados al portapapeles');
  }

  // --- Telephony formatted text getters ---

  public getTelParametrosText(): string {
    if (!this.telephonyResult || !this.telephonyResult.parametros) { return ''; }
    let p = this.telephonyResult.parametros;
    let lines = [];
    lines.push('--- Parametros ---');
    if (p.SIP_DN && p.SIP_DN.valor !== undefined) { lines.push('SIP DN: ' + p.SIP_DN.valor); }
    if (p.SIP_URI && p.SIP_URI.valor !== undefined) { lines.push('SIP URI: ' + p.SIP_URI.valor); }
    if (p.SIP_TIMEZONE && p.SIP_TIMEZONE.valor !== undefined) { lines.push('Timezone: ' + p.SIP_TIMEZONE.valor); }
    if (p.SIP_LOCATION && p.SIP_LOCATION.valor !== undefined) { lines.push('Location: ' + p.SIP_LOCATION.valor); }
    if (p.SIP_SUBDOMAIN && p.SIP_SUBDOMAIN.valor !== undefined) { lines.push('Subdomain: ' + p.SIP_SUBDOMAIN.valor); }
    if (p.NUMBER_OF_CONTACTS && p.NUMBER_OF_CONTACTS.valor !== undefined) { lines.push('Número de Contactos: ' + p.NUMBER_OF_CONTACTS.valor); }
    if (p.CONTACTS && p.CONTACTS.valor !== undefined) { lines.push('Contactos: ' + p.CONTACTS.valor); }
    return lines.join('\n');
  }

  public getTelValidacionesText(): string {
    if (!this.telephonyResult || !this.telephonyResult.parametros) { return ''; }
    const p = this.telephonyResult.parametros;
    let lines = [];
    lines.push('--- Validaciones ---');
    const validations = [
      { key: 'SIP_FIRST_NAME', label: 'Sip First Name' },
      { key: 'SIP_LAST_NAME', label: 'SIP Last Name' },
      { key: 'SIP_EP_NAME', label: 'SIP EP Name' },
      { key: 'ACCOUNT_STATUS', label: 'Account Status' },
      { key: 'REGISTERED', label: 'Registrado' }
    ];
    for (let i = 0; i < validations.length; i++) {
      const v = validations[i];
      const param = p[v.key];
      if (param) {
        if (param.pass === true || param.pass === 1) {
          lines.push(v.label + ': Correcto');
        } else if (param.pass === false || param.pass === 0) {
          lines.push(v.label + ': Incorrecto');
        }
        if (param.valor !== undefined) { lines.push('Valor: ' + param.valor); }
        if (param.esperado !== undefined) { lines.push('Esperado: ' + param.esperado); }
      }
    }
    return lines.join('\n');
  }

  public getTelResumenText(): string {
    if (!this.telephonyResult) { return ''; }
    const lines = [];
    lines.push('--- Resumen ---');
    lines.push(this.telephonyResult.resumen || this.telephonyResult.summary || '');
    return lines.join('\n');
  }

  public copyAllTel(): void {
    if (!this.telephonyResult) { return; }
    const sections = [
      this.getTelParametrosText(),
      this.getTelValidacionesText(),
      this.getTelResumenText()
    ].filter(function(s) { return s && s.length > 0; });
    this.copyToClipboard(sections.join('\n\n'), 'Todos los resultados Telefonía copiados al portapapeles');
  }

  // --- Shared clipboard helper ---

  private copyToClipboard(text: string, msg: string): void {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    this.snackBar.open(msg, 'Cerrar', { duration: 2000 });
  }
}
